import {
  isUnmappedPageId,
  useMoveProposalPlanGroup,
} from "@platform/mpx-quotes-grid";
import { useCallback, useMemo, useState } from "react";

import type { BenefitPlanGroup } from "@platform/btx-plan-designer";
import type {
  MovePlanMenuConfig,
  MovePlanTarget,
} from "@platform/mpx-quotes-grid";

interface UseMovePlanDialogParams {
  ecoRfpId: string;
  /** The requested plan group backing the currently displayed page. */
  currentBenefitPlanGroupId?: string;
  /** All requested plan groups for the currently displayed coverage type. */
  benefitPlanGroupsForCoverage: BenefitPlanGroup[];
  onError: (err: Error) => void;
}

interface UseMovePlanDialogResult {
  movePlanTarget: MovePlanTarget | null;
  movePlanMenuConfig: MovePlanMenuConfig;
  handleMovePlanConfirm: (targetBenefitPlanGroupId: string) => void;
  closeMovePlanDialog: () => void;
}

/**
 * Encapsulates the "Move Plan Group" carrier-header menu action: moves a carrier's
 * plan group to a different requested plan group's SBS page. Extracted to
 * keep Quotes below the complexity limit (same rationale as useRatesDrawerState).
 */
export const useMovePlanDialog = ({
  ecoRfpId,
  currentBenefitPlanGroupId,
  benefitPlanGroupsForCoverage,
  onError,
}: UseMovePlanDialogParams): UseMovePlanDialogResult => {
  const [movePlanTarget, setMovePlanTarget] = useState<MovePlanTarget | null>(
    null,
  );

  // The coverage's requested pages, which movePlanDestinations narrows per carrier
  // column. The synthetic Unmapped Plans page (btx-unmapped-plans) is dropped — it can
  // never be a move target — but the page on screen is kept: whether the coverage
  // requests a single product type is a question about the whole coverage, and
  // `currentPageId` below is what excludes the page a group already sits on.
  const requestedPages = useMemo(
    () =>
      benefitPlanGroupsForCoverage
        .filter((group) => !isUnmappedPageId(group.id))
        .map((group) => ({ id: group.id, productType: group.productType })),
    [benefitPlanGroupsForCoverage],
  );
  const closeMovePlanDialog = useCallback(() => setMovePlanTarget(null), []);

  const moveProposalPlanGroup = useMoveProposalPlanGroup({ onError });

  const handleMovePlanConfirm = useCallback(
    (targetBenefitPlanGroupId: string) => {
      if (!movePlanTarget) return;
      moveProposalPlanGroup.mutate({
        rfpId: ecoRfpId,
        groupId: movePlanTarget.groupId,
        rfpBenefitPlanGroupId: targetBenefitPlanGroupId,
      });
      // The column moves in the cache on mutate, so the dialog has nothing
      // left to show; a failed move rolls back and raises the error toast.
      closeMovePlanDialog();
    },
    [movePlanTarget, moveProposalPlanGroup, ecoRfpId, closeMovePlanDialog],
  );

  const movePlanMenuConfig = useMemo<MovePlanMenuConfig>(
    () => ({
      requestedPages,
      currentPageId: currentBenefitPlanGroupId,
      onMovePlan: setMovePlanTarget,
    }),
    [requestedPages, currentBenefitPlanGroupId],
  );

  return {
    movePlanTarget,
    movePlanMenuConfig,
    handleMovePlanConfirm,
    closeMovePlanDialog,
  };
};
